import { HStack, IconButton, Text, Flex, Box, Menu, MenuButton, MenuList, MenuItem, Avatar, Divider, Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useColorMode } from '@chakra-ui/react'
import { Menu as MenuIcon, Bell, LogOut, User, Moon, Sun } from 'lucide-react'
import { User as AppUser } from '@/types'
import { useLocation, Link } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TYPO_BODY, TYPO_CAPTION, TYPO_TITLE } from '@/lib/typography'

const HEADER_ICON_SIZE = 16
const HEADER_MENU_ICON_SIZE = 15

interface HeaderProps {
  onOpen: () => void
  user: AppUser | null
  onLogout: () => void
}

export function Header({ onOpen, user, onLogout }: HeaderProps) {
  const colors = useThemeColors()
  const location = useLocation()
  const pathname = location.pathname
  const segments = (pathname || '/').split('/').filter(Boolean)
  const toLabel = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const title = segments.length > 0 ? toLabel(segments[0]) : 'Dashboard'
  const crumbs = segments.map((seg, idx) => ({
    label: toLabel(seg),
    href: '/' + segments.slice(0, idx + 1).join('/'),
  }))
  const { colorMode, toggleColorMode } = useColorMode()

  return (
    <Box bg={colors.cardBg} borderBottom="1px" borderColor={colors.border} px={5} py={3}>
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={2}>
          <IconButton
            aria-label="Open menu"
            icon={<MenuIcon size={HEADER_ICON_SIZE} />}
            variant="ghost"
            size="sm"
            display="inline-flex"
            onClick={onOpen}
          />
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize={TYPO_TITLE} fontWeight="semibold" lineHeight="short">{title}</Text>
            {segments.length > 0 && (
              <Breadcrumb fontSize={TYPO_CAPTION} color={colors.text} separator="/">
                <BreadcrumbItem key="home">
                  <BreadcrumbLink as={Link} to="/dashboard">Home</BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c, i) => (
                  <BreadcrumbItem key={c.href} isCurrentPage={i === crumbs.length - 1}>
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink as={Link} to={c.href}>{c.label}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        <HStack spacing={1}>
          <IconButton
            aria-label="Toggle color mode"
            icon={colorMode === 'light' ? <Moon size={HEADER_ICON_SIZE} /> : <Sun size={HEADER_ICON_SIZE} />}
            variant="ghost"
            size="sm"
            onClick={toggleColorMode}
          />
          <IconButton
            aria-label="Notifications"
            icon={<Bell size={HEADER_ICON_SIZE} />}
            variant="ghost"
            size="sm"
          />
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              fontSize={TYPO_BODY}
              leftIcon={<Avatar size="xs" name={user?.user_login || ''} />}
            >
              {user?.user_login ? user.user_login.split(' ')[0] : ''}
            </MenuButton>
            <MenuList fontSize={TYPO_BODY}>
              <MenuItem icon={<User size={HEADER_MENU_ICON_SIZE} />} as={Link} to="/profile">Profile</MenuItem>
              <Divider />
              <MenuItem icon={<LogOut size={HEADER_MENU_ICON_SIZE} />} onClick={onLogout}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  )
}

export default Header
